/**
 * Shared helpers for Google Places autocomplete and client-side geocoding.
 */

export const GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: "za" },
  fields: [
    "address_components",
    "geometry",
    "formatted_address",
    "place_id",
    "name",
    "types",
  ],
};

export function extractAddressComponents(components) {
  const extracted = {
    streetNumber: "",
    streetName: "",
    suburb: "",
    town: "",
    city: "",
    province: "",
    country: "South Africa",
    postalCode: "",
  };

  if (!Array.isArray(components)) {
    return extracted;
  }

  components.forEach((component) => {
    const types = component.types || [];

    if (types.includes("street_number")) {
      extracted.streetNumber = component.long_name || "";
    }
    if (types.includes("route")) {
      extracted.streetName = component.long_name || "";
    }
    if (
      types.includes("sublocality") ||
      types.includes("neighborhood") ||
      types.includes("sublocality_level_1")
    ) {
      extracted.suburb = component.long_name || "";
    }
    if (types.includes("locality")) {
      extracted.town = component.long_name || "";
    }
    if (types.includes("administrative_area_level_2")) {
      extracted.city = component.long_name || "";
    }
    if (types.includes("administrative_area_level_1")) {
      extracted.province = component.long_name || "";
    }
    if (types.includes("postal_code")) {
      extracted.postalCode = component.long_name || "";
    }
    if (types.includes("country")) {
      extracted.country = component.long_name || "South Africa";
    }
  });

  return extracted;
}

export function extractCoordinatesFromPlace(place) {
  if (place?.geometry?.location) {
    return {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
  }

  const viewport = place?.geometry?.viewport;
  if (viewport?.getCenter) {
    const center = viewport.getCenter();
    return { lat: center.lat(), lng: center.lng() };
  }

  return null;
}

const STREET_LEVEL_PLACE_TYPES = new Set([
  "street_address",
  "premise",
  "subpremise",
  "establishment",
  "point_of_interest",
  "airport",
]);

function isStreetOrPremiseLevel(place) {
  const types = place?.types || [];
  return types.some((type) => STREET_LEVEL_PLACE_TYPES.has(type));
}

function hasStreetComponents(components) {
  return Boolean(components?.streetNumber || components?.streetName);
}

function leadingStreetNumber(text) {
  const match = String(text || "")
    .trim()
    .match(/^(\d+[a-zA-Z]?)\b/);
  return match ? match[1] : "";
}

function buildStreetAddress(components, formattedAddress, name) {
  const next = { ...components };
  if (!next.streetNumber) {
    next.streetNumber =
      leadingStreetNumber(formattedAddress) || leadingStreetNumber(name);
  }
  return buildFullAddressFromComponents(next);
}

/**
 * True when `candidate` is only the typed / leading fragment of `fullText`
 * (e.g. "12 Riv" or "12 Rivonia Road" vs the full formatted suggestion).
 */
export function isLeadingAddressFragment(candidate, fullText) {
  if (!candidate || !fullText) {
    return false;
  }

  const selected = candidate.trim().toLowerCase();
  const full = fullText.trim().toLowerCase();
  if (!selected || selected === full) {
    return false;
  }
  if (full.startsWith(selected)) {
    return true;
  }

  const selectedHead = selected.split(",")[0].trim();
  const fullHead = full.split(",")[0].trim();
  return (
    Boolean(selectedHead) &&
    selectedHead === fullHead &&
    selected.length < full.length
  );
}

/**
 * Collapse Google Autocomplete's partial text highlight so the full
 * suggestion stays in the input after the user picks it.
 */
export function applySelectedAddressToInput(inputEl, address) {
  if (!inputEl || typeof address !== "string") {
    return;
  }

  inputEl.value = address;
  const end = address.length;
  try {
    inputEl.setSelectionRange(end, end);
  } catch {
    // Some input types reject setSelectionRange.
  }
}

function buildFullAddressFromComponents(components) {
  const parts = [];
  const street = [components.streetNumber, components.streetName]
    .filter(Boolean)
    .join(" ");

  if (street) {
    parts.push(street);
  }
  if (components.suburb) {
    parts.push(components.suburb);
  }

  const locality = components.town || components.city;
  if (locality) {
    parts.push(locality);
  }
  if (components.province) {
    parts.push(components.province);
  }

  return parts.join(", ");
}

function formatLocalityAddress(primary, components) {
  const name = (primary || "").trim();
  if (!name) {
    return "";
  }

  const parts = [name];
  if (components.province && !name.includes(components.province)) {
    parts.push(components.province);
  } else if (!name.includes("South Africa")) {
    parts.push("South Africa");
  }

  return parts.join(", ");
}

/**
 * Resolve a user-facing address label from a Google Place result.
 * Google sometimes returns renamed official labels (e.g. "KuGompo City")
 * that differ from what the user selected (e.g. "East London").
 *
 * Do not prefer the live input value when it is only the typed prefix —
 * React controlled inputs often still hold that fragment when
 * `place_changed` fires. Likewise, `place.name` is usually just the
 * first line of the suggestion, not the full address.
 */
export function resolvePlaceDisplayAddress(place, selectedText = "") {
  const formattedAddress = (place?.formatted_address || place?.name || "").trim();
  const selected = (selectedText || "").trim();
  const name = (place?.name || "").trim();
  const components = extractAddressComponents(place?.address_components);
  const streetLevel =
    isStreetOrPremiseLevel(place) || hasStreetComponents(components);

  if (streetLevel) {
    if (hasStreetComponents(components)) {
      return (
        buildStreetAddress(components, formattedAddress, name) ||
        formattedAddress
      );
    }
    return formattedAddress || formatLocalityAddress(name, components);
  }

  const selectedIsGenuineAlternative =
    selected &&
    selected !== formattedAddress &&
    place?.geometry &&
    !isLeadingAddressFragment(selected, formattedAddress) &&
    !isLeadingAddressFragment(selected, name);

  if (selectedIsGenuineAlternative) {
    return selected;
  }

  if (
    name &&
    name !== formattedAddress &&
    !isLeadingAddressFragment(name, formattedAddress)
  ) {
    const localityDisplay = formatLocalityAddress(name, components);
    if (localityDisplay) {
      return localityDisplay;
    }
  }

  const localityDisplay = formatLocalityAddress(
    components.town || components.city || components.suburb,
    components
  );
  if (localityDisplay) {
    return localityDisplay;
  }

  return formattedAddress;
}

export function placeToAddressData(place, selectedText = "") {
  const coordinates = extractCoordinatesFromPlace(place);
  const address = resolvePlaceDisplayAddress(place, selectedText);

  return {
    address,
    coordinates,
    googlePlaceId: place?.place_id || null,
    addressComponents: extractAddressComponents(place?.address_components),
    googlePlaceData: place || {},
    addressConfidence: coordinates ? "high" : "low",
  };
}
